import React, { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TableHorizontal, { InfoObject } from '../misc/TableHorizontal';
import FilterBottomComponent from '../misc/FilterBottomComponent';
import { listAccountPasswords, exportPasswordsFilter, exportPasswordsSelected } from '../../store/PasswordAdminAction'
import { clearClassGroups, getClassGroups, getSchoolUnits } from '../../store/FilterAction';
import { CacheRequestState } from '../../models/RequestState';
import { CoreDisplayableRef, PageResult } from '../../models/CoreModels';
import { CorePasswordAdminAccount, CorePasswordAdminAccountRole } from '../../models/CorePasswordAdminModels';
import StatusCode from '../../util/StatusCode';
import { useSearchParams } from '../../../node_modules/react-router-dom/dist/index';
import RequestParser from '../../util/RequestParser';
import AuthorizedLink from '../auth/AuthorizedLink';
import { FlowSyncAccessPolicy, isAuthorized } from '../../models/FlowSyncAccessPolicy';
import { Col, Row, Button, Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Search from '../misc/Search';
import { debounce } from 'lodash';
import Toggle from '../misc/Toggle';
import { formatDate } from '../../util/Helper';
import SearchableSelectInput from '../misc/SearchableSelectInput';
import ModalComponent from '../misc/ModalComponent';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';

enum ExportType {
    NONE = 0,
    FILTER = 1,
    SELECTED = 2,
}

interface LayoutProps extends PropsWithChildren {
    navSection?: string,
}


const PasswordDistribution: FC<LayoutProps> = props => {
    const { t } = useTranslation(['common']);

    const dispatch = useDispatch();

    const [searchParams, setSearchParams] = useSearchParams();
    const passwordAdminAccounts = useSelector<RootState, CacheRequestState<PageResult<CorePasswordAdminAccount>>>(x => x.passwordAdmin.passwordAccounts);
    const schoolUnits = useSelector<RootState, CoreDisplayableRef[]>(x => x.filter.schoolUnits);
    const classGroups = useSelector<RootState, CoreDisplayableRef[]>(x => x.filter.classGroups);
    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);

    var filter = useMemo(() => RequestParser.parsePasswordAdminAccountRequest(searchParams), [searchParams]);
    const [search, setSearch] = useState(filter.search ?? '');
    const [selectedPaas, setSelectedPaas] = useState<{ [key: string]: boolean }>({});
    const [exportType, setExportType] = useState<ExportType>(ExportType.NONE);
    const [exportingPasswords, setExportingPasswords] = useState<boolean>(false)

    useEffect(() => {
        getSchoolUnits()(dispatch);
    }, [dispatch])

    useEffect(() => {
        if (filter.schoolUnitId) {
            getClassGroups(filter.schoolUnitId)(dispatch)
        }
        else {
            clearClassGroups()(dispatch);
        }
    }, [dispatch, filter.schoolUnitId])

    useEffect(() => {
        listAccountPasswords(filter)(dispatch);
        setSelectedPaas({})
    }, [dispatch, filter])

    const clearFilter = useCallback(() => {
        setSearch('');
        setSearchParams([]);
    }, [setSearch, setSearchParams]);

    const updateFilter = useCallback((name: string, value?: number | string | boolean | null) => {
        if (name === 'schoolUnitId') {
            searchParams.delete('classGroupId')
        }

        if (name !== 'page' && searchParams.has('page')) {
            searchParams.delete('page');
        }

        if (name === 'showExported' && value === false) {
            searchParams.delete(name);
        } else if (value !== undefined && value !== '' && value !== null) {
            if (typeof (value) === 'string') {
                searchParams.set(name, value);
            } else {
                searchParams.set(name, value.toString());
            }
        } else {
            searchParams.delete(name);
        }

        setSearchParams(searchParams);
    }, [searchParams, setSearchParams])

    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        let search = e.target.value;
        setSearch(search);
        debouncedSearch(search);
    };

    const toggleSelected = (id: string) => {
        if (selectedPaas[id]) {
            selectedPaas[id] = false;
        } else {
            selectedPaas[id] = true;
        }
        setSelectedPaas({ ...selectedPaas });
    }

    const exportPasswords = async () => {
        setExportingPasswords(true)

        switch (exportType) {
            case ExportType.FILTER: {
                await exportPasswordsFilter(filter);
                break;
            }
            case ExportType.SELECTED: {
                var ids = Object.keys(selectedPaas)
                    .filter(k => selectedPaas[k]); // only export those with true value
                await exportPasswordsSelected(ids);
                break;
            }
            default:
                break;
        }

        listAccountPasswords(filter)(dispatch);
        setSelectedPaas({});
        setExportingPasswords(false)
        setExportType(ExportType.NONE);
    }

    const hasResults = () => {
        if (passwordAdminAccounts.code === StatusCode.COMPLETE || passwordAdminAccounts.code === StatusCode.PENDING) {
            if (passwordAdminAccounts.data) {
                return passwordAdminAccounts.data.totalCount > 0;
            }
        }

        return false;
    }

    const debouncedSearch = useCallback(
        debounce(search => {
            updateFilter('search', search);
        }, 300)
        , [updateFilter]);

    const schoolUnitFilterItems = useMemo(() => {
        return schoolUnits.map(x => ({ value: x.id, label: x.title ?? "" }));
    }, [schoolUnits])

    const classGroupFilterItems = useMemo(() => {
        return classGroups.map(x => ({ value: x.id, label: x.title ?? "" }));
    }, [classGroups])

    var canExportExported = isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EXPORT_EXPORTED_PASSWORD);
    var canExport = isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EXPORT_UNEXPORTED_PASSWORD) || canExportExported;
    var selectedExportDisabled = !canExport || !Object.values(selectedPaas).some(x => x);
    var filterExportDisabled = !canExport || !hasResults() || (filter.showExported && !canExportExported);

    let tableContent: InfoObject[] = [];
    let resultCount = 0;
    if (passwordAdminAccounts.code === StatusCode.COMPLETE || passwordAdminAccounts.code === StatusCode.PENDING) {
        resultCount = passwordAdminAccounts.data?.totalCount ?? 0;
        tableContent = passwordAdminAccounts.data?.values.map(x => {
            let role = '-';
            if (x.role === CorePasswordAdminAccountRole.STAFF) {
                role = t('password.roles.teacher', { ns: 'common' });
            }
            else if (x.role === CorePasswordAdminAccountRole.STUDENT) {
                role = t('password.roles.student', { ns: 'common' });
            }

            const disableCheckbox = x.exported && !canExportExported;
            return {
                id: x.userTrackingId,
                data: [
                    { value: <Input type="checkbox" id={x.userTrackingId} checked={selectedPaas[x.userTrackingId]} onChange={() => toggleSelected(x.userTrackingId)} disabled={disableCheckbox} /> },
                    { value: <AuthorizedLink to={`/user/${x.user.id}`} accessPolicy={FlowSyncAccessPolicy.READ}>{x.user.title}</AuthorizedLink> },
                    { value: x.schoolUnit ? <AuthorizedLink to={`/schoolunit/${x.schoolUnit.id}`} accessPolicy={FlowSyncAccessPolicy.READ}>{x.schoolUnit.title}</AuthorizedLink> : '-' },
                    { value: x.classGroup ? <AuthorizedLink to={`/group/${x.classGroup.id}`} accessPolicy={FlowSyncAccessPolicy.READ}>{x.classGroup.title}</AuthorizedLink> : '-' },
                    { value: role },
                    { value: x.username ?? '-' },
                    { value: x.exported ? t('password.exported.exported', { ns: 'common', date: formatDate(x.exportedAt, false), name: x.exportedBy }) : t('password.exported.notExported', {ns:'common'}) },
                ]
            }
        }) ?? [];
    }

    return (
        <div className="container">
            <ModalComponent
                isOpen={exportType !== ExportType.NONE}
                toggleModal={() => setExportType(ExportType.NONE)}
                header={t('password.action.getPassword', { ns: 'common' })}
                update={exportPasswords}
                confirm={t('action.confirm', { ns: 'common' })}
                cancel={t('action.close', { ns: 'common' })}
                saveStatus={exportingPasswords ? StatusCode.PENDING : StatusCode.NONE}
            >
                <p>{t('password.action.exportedCount', { ns: 'common', passwordCount: (exportType === ExportType.SELECTED) ? Object.values(selectedPaas).filter(x => x).length : resultCount })}</p>
                {!canExportExported && <p><FontAwesomeIcon title="WARNING" color="#fc1" icon={['fas', 'triangle-exclamation']} />{t('password.warning', { ns: 'common' })}</p>}
            </ModalComponent>
            <div className="header--fixed large-filter">
                <h1>{t('password.title', { ns: 'common' })}</h1>
                <div>
                    <Row>
                        <Col sm="3">
                            <Search value={search} onChange={onSearch} placeholder={t('password.search.name', { ns: 'common' })} />
                        </Col>
                        <Col sm="2">
                            <SearchableSelectInput
                                items={schoolUnitFilterItems}
                                onChange={(name, value) => updateFilter(name, value)}
                                name='schoolUnitId'
                                selected={filter.schoolUnitId}
                                defaultText={t('password.search.chooseSchool', { ns: 'common' })}
                            />
                        </Col>
                        <Col sm="2">
                            <SearchableSelectInput
                                items={classGroupFilterItems}
                                onChange={(name, value) => updateFilter(name, value)}
                                name='classGroupId'
                                selected={filter.classGroupId}
                                disabled={classGroupFilterItems.length === 0}
                                defaultText={t('password.search.chooseClass', { ns: 'common' })}
                            />
                        </Col>
                        <Col sm="2">
                            <SearchableSelectInput
                                items={[{ label: t('password.roles.teacher', { ns: 'common' }), value: CorePasswordAdminAccountRole.STAFF }, { label: t('password.roles.student', { ns: 'common' }), value: CorePasswordAdminAccountRole.STUDENT }]}
                                onChange={(name, value) => updateFilter(name, value)}
                                name='role'
                                selected={filter.role}
                                defaultText={t('password.search.chooseRole', { ns: 'common' })}
                            />
                        </Col>
                        <Col sm="3">
                            <Toggle
                                id='showExported'
                                title={t('password.action.showExported', { ns: 'common' })}
                                value={filter.showExported ?? false}
                                update={(showExported) => updateFilter('showExported', showExported)}
                                disabled={passwordAdminAccounts.code === StatusCode.PENDING}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12" className="toggle">
                            {(filter.showExported && !canExportExported) && <p className="red" style={{ margin: "0.4em" }}>{t('password.unAuthorized', { ns: 'common' })}</p>}
                            <Button color="primary" className="start-request" onClick={() => setExportType(ExportType.FILTER)} disabled={filterExportDisabled}>{t('password.action.exportFiltered', { ns: 'common' })}</Button>
                            <Button color="primary" className="start-request" onClick={() => setExportType(ExportType.SELECTED)} disabled={selectedExportDisabled}>{t('password.action.exportSelected', { ns: 'common' })}</Button>
                        </Col>
                    </Row>
                </div>
            </div>
            <div className="body--scroll padding-40">
                <TableHorizontal
                    header={[
                        { value: '' },
                        { value: t('genericTableHeaders.name', { ns: 'common' }) },
                        { value: t('password.tableHeaders.school', { ns: 'common' }) },
                        { value: t('password.tableHeaders.class', { ns: 'common' }) },
                        { value: t('password.tableHeaders.role', { ns: 'common' }) },
                        { value: t('password.tableHeaders.userName', { ns: 'common' }) },
                        { value: t('password.tableHeaders.passwordStatus', { ns: 'common' }) }
                    ]}
                    info={tableContent}
                    error={passwordAdminAccounts.code === StatusCode.ERROR}
                    searching={passwordAdminAccounts.code === StatusCode.PENDING}
                    displayName={t('password.notFound.title', { ns: 'common' })}
                    noResMsg={t('password.notFound.noRes', { ns: 'common' })}
                />
            </div>
            <FilterBottomComponent
                requestState={passwordAdminAccounts}
                clearFilter={() => clearFilter()}
                updateFilter={(value) => updateFilter('page', value)}
            />
        </div>
    );
};

export default PasswordDistribution;